import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem('hakaton_admin_token') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Dashboard state
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [regToggleLoading, setRegToggleLoading] = useState(false);

  // Verify token on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    }
    // eslint-disable-next-line
  }, []);

  const verifyToken = async () => {
    try {
      await api.get('/admin/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsLoggedIn(true);
    } catch {
      localStorage.removeItem('hakaton_admin_token');
      setToken('');
      setIsLoggedIn(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const res = await api.post('/admin/login', { username, password });
      const newToken = res.data.token;
      localStorage.setItem('hakaton_admin_token', newToken);
      setToken(newToken);
      setIsLoggedIn(true);
      toast.success('Успешна пријава!');
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Грешка при пријави');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hakaton_admin_token');
    setToken('');
    setIsLoggedIn(false);
    setTeams([]);
    setStats(null);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsRes, statsRes, regRes] = await Promise.all([
        api.get('/admin/teams', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/admin/stats', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/settings/registration-status').catch(() => ({ data: { data: { пријавеОтворене: true } } }))
      ]);
      setTeams(teamsRes.data.data);
      setStats(statsRes.data.data);
      setRegistrationOpen(regRes.data.data.пријавеОтворене);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Сесија је истекла');
      } else {
        toast.error('Грешка при учитавању података');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn, fetchData]);

  const handleConfirm = async (id) => {
    setConfirmingId(id);
    try {
      const res = await api.patch(`/admin/teams/${id}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Грешка при потврди');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleDelete = async (id, teamName) => {
    if (!window.confirm(`Да ли сте сигурни да желите да обришете тим "${teamName}"?`)) {
      return;
    }

    setDeletingId(id);
    try {
      const res = await api.delete(`/admin/teams/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setTeams(prev => prev.filter(t => t._id !== id));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Грешка при брисању');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get('/admin/export-pdf', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'hakaton-km-timovi.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF успешно извезен!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Грешка при извозу PDF-а');
    }
  };

  const handleToggleRegistration = async () => {
    setRegToggleLoading(true);
    try {
      const res = await api.patch('/settings/registration-status',
        { пријавеОтворене: !registrationOpen },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistrationOpen(res.data.data.пријавеОтворене);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Грешка при промени статуса');
    } finally {
      setRegToggleLoading(false);
    }
  };

  // Filter teams
  const filteredTeams = teams.filter(team => {
    if (filter === 'ucenici') return team.типТима === 'ученици';
    if (filter === 'studenti') return team.типТима === 'студенти';
    if (filter === 'confirmed') return team.потврђен;
    if (filter === 'pending') return !team.потврђен;
    return true;
  });

  // ====== LOGIN SCREEN ======
  if (!isLoggedIn) {
    return (
      <div className="admin-login">
        <motion.div
          className="admin-login-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1>🔐 Админ панел</h1>
          <p>ХакатонКМ — Административни приступ</p>

          {loginError && <div className="login-error">{loginError}</div>}

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Корисничко име</label>
              <input
                type="text"
                className="form-input"
                placeholder="admin"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Лозинка</label>
              <input
                type="password"
                className="form-input"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loginLoading}
            >
              {loginLoading ? 'Пријављивање...' : 'Пријави се'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ====== DASHBOARD ======
  return (
    <div className="admin-dashboard">
      <div className="admin-topbar">
        <h1>📊 Админ панел — ХакатонКМ</h1>
        <div className="admin-topbar-actions">
          <button
            className={`admin-btn reg-toggle ${registrationOpen ? 'reg-open' : 'reg-closed'}`}
            onClick={handleToggleRegistration}
            disabled={regToggleLoading}
          >
            {regToggleLoading ? '...' : registrationOpen ? '🟢 Пријаве отворене' : '🔴 Пријаве затворене'}
          </button>
          <button className="admin-btn primary" onClick={handleExportPDF}>
            📄 Извези PDF
          </button>
          <button className="admin-btn" onClick={fetchData} disabled={loading}>
            🔄 {loading ? 'Учитавање...' : 'Освежи'}
          </button>
          <button className="admin-btn danger" onClick={handleLogout}>
            🚪 Одјави се
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <motion.div
          className="admin-stats"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Укупно тимова</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#22c55e' }}>{stats.confirmed}</div>
            <div className="stat-label">Потврђено</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#f59e0b' }}>{stats.pending}</div>
            <div className="stat-label">На чекању</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#00d4ff' }}>{stats.ucenici}</div>
            <div className="stat-label">Ученици</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#a855f7' }}>{stats.studenti}</div>
            <div className="stat-label">Студенти</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalMembers}</div>
            <div className="stat-label">Укупно учесника</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#ea580c' }}>{stats.totalFasting || 0}</div>
            <div className="stat-label">Посте</div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <div className="admin-filters">
        {[
          { key: 'all', label: 'Сви' },
          { key: 'ucenici', label: 'Ученици' },
          { key: 'studenti', label: 'Студенти' },
          { key: 'confirmed', label: 'Потврђени' },
          { key: 'pending', label: 'На чекању' }
        ].map(f => (
          <button
            key={f.key}
            className={`filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Teams table */}
      <div className="teams-table-wrap">
        {filteredTeams.length === 0 ? (
          <div className="no-teams">
            <p style={{ fontSize: '2rem', marginBottom: '12px' }}>📋</p>
            <p>Нема тимова за приказ</p>
          </div>
        ) : (
          <table className="teams-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Тим</th>
                <th>Тип</th>
                <th>Чланови</th>
                <th>Контакт капитена</th>
                <th>Статус</th>
                <th>Датум</th>
                <th>Акције</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filteredTeams.map((team, i) => {
                  const captain = team.чланови.find(m => m.јеКапитен);
                  return (
                    <motion.tr
                      key={team._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <td>{i + 1}</td>
                      <td><strong>{team.имеТима}</strong></td>
                      <td>
                        <span className={`team-type-badge ${team.типТима === 'ученици' ? 'ucenici' : 'studenti'}`}>
                          {team.типТима === 'ученици' ? 'Ученици' : 'Студенти'}
                        </span>
                      </td>
                      <td>
                        <div className="team-members-list">
                          {team.чланови.map((m, mi) => (
                            <div key={mi} className={m.јеКапитен ? 'captain' : ''}>
                              {m.име} {m.презиме} {m.јеКапитен ? '👑' : ''}{m.пpiости ? ' 🍽' : ''}
                            </div>
                          ))}
                          {team.чланови.some(m => m.пpiости) && (
                            <div className="fasting-info">
                              Посте: {team.чланови.filter(m => m.пpiости).length}/{team.бројЧланова}
                            </div>
                          )}
                          {team.напомена && (
                            <div className="team-note" title={team.напомена}>
                              📝 {team.напомена.length > 40 ? team.напомена.substring(0, 40) + '...' : team.напомена}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.85rem' }}>
                          <div>📧 {team.капитен.емаил}</div>
                          <div>📱 {team.капитен.телефон}</div>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${team.потврђен ? 'confirmed' : 'pending'}`}>
                          {team.потврђен ? '✓ Потврђен' : '⏳ Чека'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.85rem', color: '#a0a0c0' }}>
                        {new Date(team.датумПријаве).toLocaleDateString('sr-Latn-RS')}
                      </td>
                      <td>
                        <div className="team-actions">
                          {!team.потврђен && (
                            <button
                              className="admin-btn success"
                              onClick={() => handleConfirm(team._id)}
                              disabled={confirmingId === team._id}
                              style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                            >
                              {confirmingId === team._id ? '...' : '✓ Потврди'}
                            </button>
                          )}
                          <button
                            className="admin-btn danger"
                            onClick={() => handleDelete(team._id, team.имеТима)}
                            disabled={deletingId === team._id}
                            style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                          >
                            {deletingId === team._id ? '...' : '✕ Обриши'}
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Admin;